import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StickyNote } from 'lucide-react'
import { ReminderRow } from './ReminderRow'
import { groupOf, type ReminderGroup } from './reminderTime'
import type { Reminder } from './types'
import type { SnowflakeId } from '@/types/snowflake'

export interface ReminderActions {
  toggle: (id: SnowflakeId) => void
  remove: (id: SnowflakeId) => void
  snoozeTo: (id: SnowflakeId, dueDate: string, dueTime: string | null) => void
  updateText: (id: SnowflakeId, text: string) => void
  clearDone: () => void
}

interface Props {
  reminders: Reminder[]
  tz: string
  actions: ReminderActions
  /** 点实体深链 chip 后关闭宿主 popover(透传到每行)。 */
  onNavigate?: () => void
  /** 最近一次拉取失败:空列表时显示错误文案而非"没有提醒"(回前台/focus 会自动重拉)。 */
  loadFailed?: boolean
}

const ORDER: ReminderGroup[] = ['today', 'tomorrow', 'week', 'later']

export function ReminderList({ reminders, tz, actions, onNavigate, loadFailed }: Props) {
  const { t } = useTranslation('reminders')
  const [showDone, setShowDone] = useState(false)

  const open = reminders.filter((r) => r.status === 'open')
    .sort((a, b) => (a.dueDate + (a.dueTime ?? '')) < (b.dueDate + (b.dueTime ?? '')) ? -1 : 1)
  const done = reminders.filter((r) => r.status === 'done')

  const groups = ORDER
    .map((g) => [g, open.filter((r) => groupOf(r, tz) === g)] as const)
    .filter(([, list]) => list.length > 0)

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 6 }}>
      {groups.length === 0 && done.length === 0 && (
        <div style={{ padding: '36px 20px', textAlign: 'center', color: 'hsl(var(--muted-foreground))', fontSize: 13 }}>
          <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center', opacity: 0.8 }}><StickyNote size={28} /></div>
          {loadFailed ? t('loadFailed') : t('emptyState')}
        </div>
      )}
      {groups.map(([g, list]) => (
        <div key={g}>
          <div style={{ padding: '12px 16px 3px', fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase' }}>
            {t(`groups.${g}`)}<span style={{ fontWeight: 600, marginLeft: 6, color: 'hsl(var(--muted-foreground))' }}>{list.length}</span>
          </div>
          {list.map((r) => (
            <ReminderRow key={r.id} rem={r} tz={tz}
              onToggle={() => actions.toggle(r.id)}
              onSnoozeTo={(d, tm) => actions.snoozeTo(r.id, d, tm)}
              onDelete={() => actions.remove(r.id)}
              onSaveText={(text) => actions.updateText(r.id, text)}
              onNavigate={onNavigate} />
          ))}
        </div>
      ))}
      {groups.length > 0 && (
        <div style={{ fontSize: 11, color: '#6C5CE0', fontWeight: 500, padding: '10px 16px 2px', textAlign: 'center' }}>{t('editHint')}</div>
      )}
      {done.length > 0 && (
        <div style={{ borderTop: '1px solid hsl(var(--border))', marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '9px 16px 2px' }}>
            <button onClick={() => setShowDone((v) => !v)} style={{
              border: 'none', background: 'none', cursor: 'pointer', padding: 0,
              fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase',
            }}>{showDone ? '▾' : '▸'} {t('completed')} {done.length}</button>
            <div style={{ flex: 1 }} />
            <button onClick={actions.clearDone} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#6C5CE0', padding: 0 }}>{t('clear')}</button>
          </div>
          {showDone && done.map((r) => (
            <ReminderRow key={r.id} rem={r} tz={tz}
              onToggle={() => actions.toggle(r.id)} onSnoozeTo={() => {}} onDelete={() => actions.remove(r.id)}
              onSaveText={(text) => actions.updateText(r.id, text)}
              onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  )
}
